import { Button, Spinner } from '@heroui/react';
import { RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import React, { FC } from 'react';

import { useHeroToast } from '../../../../../hooks';
import {
  AdCreativeListItem,
  useDeleteAdCreatives,
  useFetchAdCreative,
  useGetAdCreatives,
} from '../../../../../queries';
import { TEMPLATES } from '../../../config/templates';
import { useAdsGeneratorStore } from '../../../model/store';
import { applySavedTemplate } from '../../../utils/scene';
import { PanelSection } from '../../common';
import { CardHint, CardTitle } from '../../PlatformStep/PlatformStep.styled';
import { EmptyPanelHint, SavedCard, SavedRow, TemplateCard, TemplateThumb } from '../Editor.styled';

interface TemplatesPanelProps {
  /** Przenosi do kroku AI — generowanie ma jedno miejsce w całym edytorze. */
  onGenerate: () => void;
}

/**
 * Szablony wbudowane przeliczają się na wymiary bieżącej planszy, a te zapisane
 * w bazie są skalowane proporcjonalnie z formatu, w którym powstały.
 */
export const TemplatesPanel: FC<TemplatesPanelProps> = ({ onGenerate }) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const replaceElements = useAdsGeneratorStore((state) => state.replaceElements);
  const { data: savedTemplates = [], isLoading, isFetching, refetch } = useGetAdCreatives('template');
  const fetchCreative = useFetchAdCreative();
  const { mutate: removeTemplates } = useDeleteAdCreatives();
  const { showError } = useHeroToast();

  if (!scene) return null;

  const applySaved = async (template: AdCreativeListItem): Promise<void> => {
    try {
      const details = await fetchCreative(template.id);
      const result = applySavedTemplate(details, { width: scene.width, height: scene.height });
      replaceElements(result.elements, result.background);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się wczytać szablonu');
    }
  };

  return (
    <>
      <PanelSection title="Generuj szablon">
        <p className="text-xs text-gray-500 leading-relaxed">
          Opisz, co ma być na kreacji, albo dołącz plik poglądowy. AI ułoży planszę w naszym formacie,
          gotową do dalszej pracy.
        </p>
        <Button
          size="sm"
          radius="sm"
          className="bg-black text-white"
          startContent={<Sparkles size={16} />}
          onPress={onGenerate}
        >
          Generuj szablon
        </Button>
      </PanelSection>

      <PanelSection title="Szablony wbudowane">
        <p className="text-xs text-gray-500 leading-relaxed">
          Wybranie szablonu zastępuje bieżącą treść planszy. Cofniesz to skrótem Ctrl+Z.
        </p>
        <div className="flex flex-col gap-2">
          {TEMPLATES.map((template) => (
            <TemplateCard
              key={template.id}
              type="button"
              onClick={() => {
                const result = template.build(scene.width, scene.height);
                replaceElements(result.elements, result.background);
              }}
            >
              <TemplateThumb
                $from={template.preview.from}
                $to={template.preview.to}
                $accent={template.preview.accent}
              />
              <span>
                <CardTitle>{template.label}</CardTitle>
                <CardHint>{template.description}</CardHint>
              </span>
            </TemplateCard>
          ))}
        </div>
      </PanelSection>

      <PanelSection
        title="Szablony zapisane"
        action={(
          <Button
            size="sm"
            radius="sm"
            isIconOnly
            variant="light"
            aria-label="Odśwież listę szablonów"
            isLoading={isFetching}
            onPress={() => refetch()}
          >
            <RefreshCw size={14} />
          </Button>
        )}
      >
        {isLoading && <Spinner size="sm" />}
        {!isLoading && savedTemplates.length === 0 && (
          <EmptyPanelHint>
            Nie ma jeszcze własnych szablonów. Ułóż planszę i zapisz ją przyciskiem
            {' '}
            „Zapisz jako szablon" w górnym pasku.
          </EmptyPanelHint>
        )}
        <div className="flex flex-col gap-2">
          {savedTemplates.map((template) => (
            <SavedRow key={template.id}>
              <SavedCard type="button" onClick={() => applySaved(template)}>
                <img src={template.preview || undefined} alt="" />
                <span>
                  <CardTitle>{template.name}</CardTitle>
                  <CardHint>{`${template.width} x ${template.height} px`}</CardHint>
                </span>
              </SavedCard>
              <Button
                size="sm"
                radius="sm"
                isIconOnly
                variant="light"
                color="danger"
                aria-label="Usuń szablon"
                onPress={() => removeTemplates(
                  { ids: [template.id] },
                  { onError: (error) => showError(error.message || 'Nie udało się usunąć szablonu') },
                )}
              >
                <Trash2 size={14} />
              </Button>
            </SavedRow>
          ))}
        </div>
      </PanelSection>
    </>
  );
};
